import React, { useEffect, useState } from "react";
import { Copy, Check, X } from "lucide-react";
import { toast } from "sonner";

const CODE = "DUBAI10";

/**
 * Floating coupon banner — luxury accent, brand colors, persistent
 * across the entire site until the user dismisses it. Click "copy"
 * stores the code in localStorage so checkout auto-applies it.
 */
export default function FloatingCouponBanner() {
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [show, setShow] = useState(false);
  const [hiddenForCta, setHiddenForCta] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem("dp_coupon_dismissed") === "1") {
      setDismissed(true);
      return;
    }
    // Appear after 1s so it doesn't fight with hero animations
    const t = setTimeout(() => setShow(true), 1000);

    // On mobile (<768px), the StickyMobileCta appears after scrollY > 700.
    // Hide the floating banner there to avoid overlap with the bottom bar.
    const onScroll = () => {
      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      setHiddenForCta(isMobile && window.scrollY > 700);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(CODE);
      localStorage.setItem("dp_discount", CODE);
      window.dispatchEvent(new Event("dp:discount-changed"));
      setCopied(true);
      toast.success(`Code ${CODE} copied · auto-applied at checkout`);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Could not copy. Please copy manually: " + CODE);
    }
  };

  const dismiss = () => {
    localStorage.setItem("dp_coupon_dismissed", "1");
    setDismissed(true);
  };

  if (dismissed || !show || hiddenForCta) return null;

  return (
    <div
      data-testid="floating-coupon-banner"
      className="fixed left-3 md:left-6 bottom-20 md:bottom-24 z-[80] max-w-[260px] md:max-w-[300px] fade-in-up"
    >
      <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-[#0e0e10] text-white border border-white/10">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss coupon"
          data-testid="floating-coupon-close"
          className="absolute top-2.5 right-2.5 w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center z-10"
        >
          <X size={12} />
        </button>

        <div className="px-5 pt-5 pb-4">
          <div className="inline-flex items-center gap-1.5 bg-[#d8f06b] text-[#0e0e10] text-[9px] uppercase tracking-[0.22em] font-extrabold px-2.5 py-1 rounded-full">
            🎁 First-order perk
          </div>
          <div className="mt-3 text-[15px] font-black leading-tight">
            10% off your <span className="text-[#d8f06b]">Dubai Pillows</span> order.
          </div>
          <p className="mt-1.5 text-[11px] text-white/65 leading-snug">
            Tap to copy your code — auto-applied at checkout.
          </p>

          <button
            type="button"
            onClick={copy}
            data-testid="floating-coupon-copy"
            className={`mt-3 w-full flex items-center justify-between gap-2 rounded-full px-4 py-2.5 font-extrabold transition ${
              copied
                ? "bg-[#22c55e] text-white"
                : "bg-[#d8f06b] text-[#0e0e10] hover:bg-white"
            }`}
          >
            <span className="text-[14px] tabular-nums tracking-[0.18em]">
              {CODE}
            </span>
            <span className="text-[10px] uppercase tracking-wider inline-flex items-center gap-1.5">
              {copied ? (
                <>
                  <Check size={12} /> Copied
                </>
              ) : (
                <>
                  <Copy size={12} /> Copy
                </>
              )}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
